import type {
  ChatConversationDataObject,
  ChatData,
  ChatDataProvider,
} from "@afassoftware/components";
import { historyEntryTitle } from "./conversation-groups.js";
import type { ChatHistoryStateEntry, ChatLabels, ChatState } from "./types.js";

/** What the provider needs from the surrounding panel to act on a conversation. */
export type ConversationProviderHost = {
  /** Resume the entry at this position in `state.history`. */
  resumeConversation(index: number): void;
  /** Run Help's remove flow, including its confirmation dialog. */
  removeConversation(sessionToken: string): Promise<void>;
};

function toDate(value: unknown): Date | undefined {
  if (typeof value !== "string" || !value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Maps Help history entries onto the shape `afas-chat-overview` renders.
 *
 * Entries the server marked `unavailable` are dropped: resuming one is refused anyway and
 * `ChatConversationDataObject` has no disabled state, so listing them would only offer a
 * row that silently does nothing.
 */
export function toConversations(
  entries: readonly ChatHistoryStateEntry[],
  labels: ChatLabels | undefined,
): ChatConversationDataObject[] {
  return entries
    .filter((entry) => entry.unavailable !== true && Boolean(entry.sessionToken))
    .map((entry) => {
      const created = toDate(entry.createdUtc);
      const last = toDate(entry.updatedUtc) ?? created;
      // Built by spreading rather than assigning undefined: exactOptionalPropertyTypes
      // rejects an explicit undefined for an optional Date.
      return {
        id: entry.sessionToken,
        title: historyEntryTitle(entry.title, labels),
        ...(last ? { lastMessageDate: last } : {}),
        ...(created ? { createdDate: created } : {}),
      };
    });
}

/**
 * Signature of everything the overview renders, so a store update that leaves the list
 * visually identical does not trigger `data-changed`. The overview guards its own render
 * with a signature since 3.x, but the event still makes it re-read the provider, so the
 * store's streaming updates (one per token) are kept away from it here.
 */
function signatureOf(
  conversations: readonly ChatConversationDataObject[],
  activeId: string,
): string {
  const rows = conversations
    .map((c) => `${c.id}|${c.title}|${c.lastMessageDate?.getTime() ?? 0}`)
    .join(";");
  return `${activeId}#${rows}`;
}

/**
 * Help only ever dispatches `data-changed`, so every registered listener is kept in that
 * shape; the contract's generic callback type is narrowed to it on registration.
 */
type DataChangedListener = (event: CustomEvent<void>) => void;

/**
 * Adapts the Help session store to the `ChatDataProvider` contract of `afas-chat-overview`.
 *
 * Deliberately only implements the conversation half of the contract. Help renders its own
 * thread and composer, not `afas-chat`, so the message methods exist to satisfy the type.
 *
 * The package exports `BaseAfasDataProvider` with the listener bookkeeping below, but
 * importing it as a value would pull the component package into this module, which the
 * Node test runner loads without a DOM. The few lines are kept here instead.
 */
export class HelpConversationProvider implements ChatDataProvider {
  private listeners = new Set<DataChangedListener>();

  private conversations: ChatConversationDataObject[] = [];

  private entries: readonly ChatHistoryStateEntry[] = [];

  private activeId = "";

  private signature = "";

  loaded = false;

  error = false;

  constructor(private readonly host: ConversationProviderHost) {}

  /** Re-reads the store state and notifies the overview only when the list actually changed. */
  setState(state: ChatState | null): void {
    this.loaded = state?.historyStatus === "ready";
    this.error = state?.historyStatus === "error";
    this.entries = state?.history ?? [];
    this.activeId = state?.sessionToken ?? "";

    const conversations = toConversations(this.entries, state?.labels);
    const signature = signatureOf(conversations, this.activeId);
    if (signature === this.signature) return;

    this.signature = signature;
    this.conversations = conversations;
    this.emitDataChanged();
  }

  getConversations(): ChatConversationDataObject[] {
    return this.conversations;
  }

  getActiveConversationId(): string | undefined {
    return this.activeId || undefined;
  }

  selectConversation(id: string): Promise<void> {
    const index = this.entries.findIndex((entry) => entry.sessionToken === id);
    if (index >= 0) this.host.resumeConversation(index);
    return Promise.resolve();
  }

  deleteConversation(id: string): Promise<void> {
    return this.host.removeConversation(id);
  }

  /** Part of the contract for `afas-chat`, which Help does not render. */
  addMessage(): Promise<boolean> {
    return Promise.resolve(false);
  }

  getData(): ChatData {
    return { id: this.activeId, messages: [] };
  }

  getDataAsync(): Promise<ChatData> {
    return Promise.resolve(this.getData());
  }

  addEventListener: ChatDataProvider["addEventListener"] = (type, callback) => {
    if (type !== "data-changed") return;
    this.listeners.add(callback as unknown as DataChangedListener);
  };

  removeEventListener: ChatDataProvider["removeEventListener"] = (
    type,
    callback,
  ) => {
    if (type !== "data-changed") return;
    this.listeners.delete(callback as unknown as DataChangedListener);
  };

  dispose(): void {
    this.listeners.clear();
  }

  private emitDataChanged(): void {
    const event = new CustomEvent<void>("data-changed");
    for (const listener of this.listeners) listener(event);
  }
}
